import {
  LogoutOutlined,
  UserOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';

import Avatar from 'antd/es/avatar';
import Dropdown from 'antd/es/dropdown';
import Layout from 'antd/es/layout';
import Menu from 'antd/es/menu';
import Flex from 'antd/es/flex';
import { useMemo, useState, lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router';
import logoSvg from 'src/assets/logo-white.svg';
import config from 'src/config';
import { i18n } from 'src/i18n';
import authActions from 'src/modules/auth/authActions';
import authSelectors from 'src/modules/auth/authSelectors';
import PermissionChecker from 'src/modules/auth/permissionChecker';
import { useDispatch, useSelector } from 'src/modules/hooks';
import { buildMenu, selectedKeys } from 'src/view/layout/helpers';
import HeaderWrapper from 'src/view/layout/styles/HeaderWrapper';
import { mainMenu, secondaryMenu } from 'src/view/menus';
import Button from 'antd/es/button';
import Tooltip from 'antd/es/tooltip';

const ProductCategoryModal = lazy(
  () => import('src/view/tenant/view/ProductCategoryModal'),
);

const { Header: AntHeader } = Layout;

const Header = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [isModalOpen, setModalOpen] = useState(false);

  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const currentUser = useSelector(authSelectors.selectCurrentUser);

  const logoUrl = useSelector(authSelectors.selectLogoUrl);
  const userDropdownText = useSelector(
    authSelectors.selectCurrentUserNameOrEmailPrefix,
  );
  const tenantLogo = currentTenant?.logo[0]?.downloadUrl;

  const permissionChecker = useMemo(() => {
    return new PermissionChecker(currentTenant, currentUser);
  }, [currentTenant, currentUser]);

  const doSignout = () => {
    dispatch(authActions.doSignout());
  };

  const selectedMenu = useMemo(() => {
    return selectedKeys(location.pathname, mainMenu);
  }, [location.pathname]);

  const getItems = () => {
    const menu = buildMenu(
      secondaryMenu.filter(item =>
        permissionChecker.match(item.permissionRequired),
      ),
    );

    return [
      ...menu,
      { type: 'divider' },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: i18n('auth.signout'),
        onClick: doSignout,
      },
    ];
  };

  const renderMenu = () => {
    return buildMenu(
      mainMenu.filter(menu => permissionChecker.match(menu.permissionRequired)),
    );
  };

  return (
    <>
      <HeaderWrapper>
        <AntHeader>
          <div className='logo'>
            <Link to='/'>
              <img
                alt={i18n('app.title')}
                loading='eager'
                decoding='async'
                fetchPriority='high'
                src={logoUrl || logoSvg}
              />
            </Link>
          </div>

          <Menu
            mode='horizontal'
            theme='dark'
            selectedKeys={selectedMenu}
            items={renderMenu()}
          />

          <Flex gap='15px' align='center'>
            {currentTenant?.activeCategory && (
              <Tooltip title={i18n(`productCategories.title`)}>
                <Button
                  onClick={() => setModalOpen(true)}
                  icon={<ApartmentOutlined />}
                  type='primary'
                >
                  {i18n(`productCategories.${currentTenant?.activeCategory}`)}
                </Button>
              </Tooltip>
            )}

            <Dropdown
              className='user-dropdown'
              menu={{ items: getItems() }}
              trigger={['click']}
            >
              <div className='user-dropdown-content'>
                <Avatar
                  style={{ backgroundColor: '#f5f5f5' }}
                  className='user-dropdown-avatar'
                  size={26}
                  src={tenantLogo || undefined}
                  alt='avatar'
                  icon={tenantLogo ? undefined : <UserOutlined />}
                />
                <span className='user-dropdown-text'>
                  <span>{userDropdownText}</span>{' '}
                  {['multi', 'multi-with-subdomain'].includes(
                    config.tenantMode,
                  ) && (
                    <span className='user-dropdown-text-tenant'>
                      {currentTenant && currentTenant.name}
                    </span>
                  )}
                </span>
              </div>
            </Dropdown>
          </Flex>
        </AntHeader>
      </HeaderWrapper>
      <Suspense fallback={null}>
        <ProductCategoryModal
          currentTennant={currentTenant}
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
        />
      </Suspense>
    </>
  );
};

export default Header;
